/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import * as React from 'react';

import { serialize, TextRange } from '@gitbook/slate';
import type { EditorContainer } from '@gitbook/slate-react';
import { useEventCallback } from '@gitbook/util-react-hooks';
/**
 * Text selection captured from the page editor for agent context.
 */
export interface AgentSelectionReference {
    /**
     * Slate text range selected in the editor.
     */
    range: TextRange;
    /**
     * Plain text extracted from the selected range.
     */
    text: string;
}

/**
 * Keep a stable snapshot of the latest defined text selection in the editor.
 */
export function useSelectionSnapshot(props: {
    editor: EditorContainer | null;
    pageId: string | null | undefined;
}) {
    const { editor, pageId } = props;

    const [selectionSnapshot, setSelectionSnapshot] =
        React.useState<AgentSelectionReference | null>(null);
    const lastSelectionSignatureRef = React.useRef<string | null>(null);
    const isMouseDownInEditorRef = React.useRef(false);

    const resetSnapshot = useEventCallback(() => {
        lastSelectionSignatureRef.current = null;
        setSelectionSnapshot(null);
    });

    const syncSelectionSnapshot = useEventCallback(() => {
        const nextSelectionSnapshot = getSelectionReference(editor);

        if (!nextSelectionSnapshot) {
            resetSnapshot();
            return;
        }

        const nextSelectionSignature = getSelectionSignature(nextSelectionSnapshot);

        if (lastSelectionSignatureRef.current === nextSelectionSignature) {
            return;
        }

        lastSelectionSignatureRef.current = nextSelectionSignature;
        setSelectionSnapshot((previousSelectionSnapshot) => {
            if (
                previousSelectionSnapshot &&
                getSelectionSignature(previousSelectionSnapshot) === nextSelectionSignature
            ) {
                return previousSelectionSnapshot;
            }

            return nextSelectionSnapshot;
        });
    });

    React.useEffect(() => {
        lastSelectionSignatureRef.current = null;
        setSelectionSnapshot(null);
        syncSelectionSnapshot();
    }, [editor, pageId, syncSelectionSnapshot]);

    // Sync the selection snapshot on editor mouse events and periodically
    // while the mouse is up to keep it up to date with user interactions in the editor.
    React.useEffect(() => {
        if (!editor) {
            return;
        }

        const editorElement = editor.elementRef.current;
        if (!editorElement) {
            return;
        }

        const handleMouseDown = () => {
            isMouseDownInEditorRef.current = true;
        };
        const handleMouseUp = () => {
            if (!isMouseDownInEditorRef.current) {
                return;
            }

            isMouseDownInEditorRef.current = false;
            syncSelectionSnapshot();
        };

        editorElement.addEventListener('mousedown', handleMouseDown);
        window.addEventListener('mouseup', handleMouseUp);

        const interval = window.setInterval(() => {
            if (isMouseDownInEditorRef.current) {
                return;
            }

            syncSelectionSnapshot();
        }, 200);

        return () => {
            isMouseDownInEditorRef.current = false;
            editorElement.removeEventListener('mousedown', handleMouseDown);
            window.removeEventListener('mouseup', handleMouseUp);
            window.clearInterval(interval);
        };
    }, [editor, syncSelectionSnapshot]);

    const clearSelectionSnapshot = useEventCallback(() => {
        resetSnapshot();
        editor?.change((change) => change.deselect());
    });

    return {
        selectionSnapshot,
        clearSelectionSnapshot,
    };
}

function getSelectionReference(editor: EditorContainer | null): AgentSelectionReference | null {
    if (!editor || !editor.value.selection) {
        return null;
    }
    const selection: TextRange = TextRange.isTextRange(editor.value.selection)
        ? editor.value.selection
        : TextRange.fromBlockRange(editor.value.selection, editor.value.document);

    // Tested before the fragment is read: `value.fragment` splits and clones the document tree, and
    // this runs on a poll — for a collapsed caret, which is most ticks, the result is thrown away.
    if (!selection.isSet || !selection.isExpanded) {
        return null;
    }

    const selectionText = editor.value.fragment.getText(' ', '').trim();

    if (!selectionText) {
        return null;
    }

    return {
        range: selection,
        text: selectionText,
    };
}

function getSelectionSignature(selectionReference: AgentSelectionReference) {
    return JSON.stringify({
        range: serialize(selectionReference.range),
        text: selectionReference.text,
    });
}
